/**
 * `apps/app` design tokens — the structural layer.
 *
 * Design: `context/proposals/app-design-system.md`.
 *
 * ## What belongs here, and what deliberately does not
 *
 * These are the values **Vera owns** — the ones a brand must not be able to
 * override, because they carry quality rather than identity: spacing rhythm,
 * the type SCALE (the ratios, not the typeface), elevation, border greys,
 * surface radius.
 *
 * Colour that carries brand identity is NOT here and must never be. A brand's
 * surface, text and accent come from the theme:
 *
 * | Want          | Read from                          | Not          |
 * |---------------|------------------------------------|--------------|
 * | page surface  | `theme.palette.background.default` | `'#FFFFFF'`  |
 * | card surface  | `theme.palette.background.paper`   | `'#FFFFFF'`  |
 * | body text     | `theme.palette.text.primary`       | `'#000000'`  |
 * | muted text    | `theme.palette.text.secondary`     | `'#6F6F6F'`  |
 * | accent / CTA  | `theme.palette.primary.main`       | a hex        |
 * | brand accent  | `theme.palette.brand[600]`         | `'#7F48DF'`  |
 *
 * That table is the whole point. There were 127 distinct hardcoded hexes in
 * this app, and each one is a place a tenant's theme is silently ignored — the
 * white-label promise is only as good as the number of colours that actually
 * route through the theme. Adding a hardcoded surface/text colour to a token
 * file would just move the problem somewhere tidier.
 *
 * (`palette.brand[*]` is safe to read HERE in a way it is not in `apps/platform`:
 * `settings/mui/brandRamp.ts` derives it from the tenant's own `primary_color`,
 * so in this app `brand` means the tenant's brand. See that file.)
 */

/**
 * Border greys — the one genuinely Vera-owned colour set.
 *
 * A hairline separator is structure, not identity, and letting it vary per
 * tenant produces the failure this replaces: the SAME light-grey border was
 * written three ways across the app (`#d1d5db` ×29, `#e5e7eb` ×13, `#e4e4e7`
 * ×5), none of them chosen deliberately, which is the largest single source of
 * visual inconsistency here.
 *
 * ⚠️ `index.css` sets `#vera-shadow-root * { border-color: hsl(var(--border)) }`.
 * That selector has ID specificity, so it beats emotion's single-class `sx`
 * styles and an `sx` `borderColor` is silently discarded — which is why ~20
 * border colours in this app are authored but never render. `--border` is
 * therefore pointed at `border.subtle`, making the global default the token.
 *
 * To override it from `sx` you need `!important`. The PROPERTY FORM is
 * irrelevant — a separate `borderColor` loses exactly as a shorthand does,
 * because specificity, not form, is what decides:
 *
 *     border: `1px solid ${border.subtle} !important`   // holds
 *     borderColor: `${border.subtle} !important`        // also holds
 *     border: '1px solid', borderColor: border.subtle   // silently loses
 *
 * (Two comments in this repo claimed the separate-property form was the fix.
 * It is not; those call sites work because they ALSO carry `!important`.)
 *
 * That has caused three separate bugs in this app's lifetime. (Those were
 * previously attributed to Tailwind preflight; preflight is DISABLED in
 * tailwind.config.js, so the cause was always this rule.)
 */
export const border = {
  subtle: '#E4E4E7',
  strong: '#D4D4D8',
} as const;

/**
 * Corner radii, in ABSOLUTE PIXELS — for corners a brand must NOT control.
 *
 * ⚠️ Read `brandRadius.ts` first. Most of what used to live here is now
 * tenant-driven: a brand sets **Corner radius** in Studio and expects the
 * screens carrying their product to change. Reach for THIS scale only when the
 * corner is a fixed piece of chrome (a checkbox glyph, a flag thumbnail, a
 * loading dot) whose shape is not a brand statement — see the classification in
 * `brandRadius.ts`.
 *
 * Never write `borderRadius: 1` or `2` in `sx`. MUI multiplies those by
 * `shape.borderRadius`, which this app resolves from the tenant's Studio
 * `border_radius` — so `borderRadius: 2` renders 16px on the default 8 and
 * 20px on a brand that picked 10. Those sites scale with the tenant
 * ACCIDENTALLY, at a factor nobody chose; `brandRadius.ts` scales deliberately,
 * at a stated one. The bulk-actions bar hit the accidental form live: 20px
 * against the cards' 12px.
 *
 * Buttons are the deliberate exception — they take the brand radius verbatim,
 * applied once in `createMuiTheme`. Fields take it clamped (`fieldMetrics.ts`).
 */
export const radius = {
  /** Chips, small controls. */
  sm: 8,
  /** Cards, toolbar controls, inputs — the app's default surface radius. */
  md: 12,
  /** Sheets, large containers. */
  lg: 16,
  pill: 999,
} as const;

/**
 * Spacing, in ABSOLUTE PIXELS — used as *the* set, not a starting point.
 *
 * Pixels because `apps/app` never overrode MUI's 8px base while `@repo/ui` /
 * `apps/platform` use 4px, so the same `spacing={3}` renders 24px here and
 * 12px there. A factor copied between apps silently doubles or halves; a pixel
 * value cannot.
 */
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

/**
 * Type scale — five sizes, nothing else.
 *
 * The app had thirteen (11/12/13/14/15/16/18/20/24/26 plus one-offs), and that
 * is why nothing reads as hierarchy: adjacent steps differing by 1px are below
 * what the eye resolves, so a page of six sizes reads as one weight of grey
 * text. Real jumps are what make an editorial layout scan.
 *
 * **14 is the floor.** 11–13px is not readable at arm's length on the phone
 * this app is reached from (an NFC tap), and 16 is the iOS no-zoom minimum for
 * anything focusable — a 14px input makes Safari zoom the whole page on focus.
 */
export const font = {
  display: 32,
  title: 24,
  heading: 20,
  body: 16,
  label: 14,
} as const;

/**
 * The certificate's SMALL LABEL treatment, in one place.
 *
 * This is what `Certificate ID` and `Product ID` are set in (`ProductDetailsPanel`),
 * and therefore what every other small label on the certificate has to match: a
 * brand's custom-field names, the `Description` label, anything that names the thing
 * below it rather than being the thing.
 *
 * ⚠️ It is a shared constant rather than four copies of the same three properties
 * because the copies had ALREADY drifted. The custom-field labels were set to
 * `fontWeight: 600` to make a field name read as a field name, which made them
 * heavier than `Certificate ID` directly above them in the same column — two
 * weights for one role, decided per file. Spencer's call is that the ID labels are
 * the reference, so the weight is simply absent here (400) and the whole treatment
 * now moves together.
 *
 * Carries NO margin. Spacing is the caller's, because the ID rows are a two-column
 * grid and a field name sits above its body — same type, different layouts.
 */
export const detailLabelSx = {
  fontSize: font.label,
  lineHeight: 1.5,
  color: 'text.secondary',
} as const;

/**
 * Elevation. Two shadows, not a scale — a card either sits on the page or is
 * lifted above it, and intermediate steps invite per-surface invention.
 *
 * Cards need SOME lift here because the page surface defaults to `#F7F7F8`
 * rather than white; on white-on-white the 1px border was the only separation,
 * which is what made the certificates list read flat.
 */
export const elevation = {
  card: '0 1px 3px rgba(16,24,40,.10), 0 1px 2px rgba(16,24,40,.06)',
  raised: '0 8px 24px rgba(16,24,40,.12)',
  /*
   * `raised`, cast UPWARD. Not a third step on the scale — the same lift, aimed the
   * other way — so the "two shadows, not a scale" rule above still holds: nothing
   * here invites an intermediate value.
   *
   * It exists because a surface that runs full-bleed and fills to the bottom of the
   * viewport has exactly ONE edge, its top. A downward shadow on such a surface falls
   * off the bottom of the screen where there is no edge, and draws nothing at the only
   * boundary the eye can see. The access gate is the case (`GateSurface`).
   *
   * TWO layers, like `card` and unlike `raised`. The near layer is what actually
   * reads as lift: a single wide blur is a haze with no edge in it, and `raised`
   * gets away with that only because the surfaces using it are inset, so their
   * own silhouette draws the edge. A full-bleed surface has no silhouette — it
   * runs off both sides — so the shadow has to supply the edge itself.
   */
  raisedUp: '0 -1px 3px rgba(16,24,40,.10), 0 -12px 28px rgba(16,24,40,.14)',
} as const;

/**
 * Typefaces.
 *
 * **`sans` is Inter, not Lato.** Lato ships no 500 and no 600 weight, so the
 * app's 55 `fontWeight: 500` and 32 `fontWeight: 600` declarations resolved to
 * 400 and 700 respectively — the whole app had two real weights. Inter carries
 * a genuine 100–900 range, so those declarations start meaning something. It is
 * also a screen-optimised grotesque, which is the closest widely-available
 * analogue to the SF Pro that makes native iOS type look tighter than a webfont
 * set at the same px.
 *
 */
export const typeface = {
  /**
   * System-first, so Apple devices render the REAL SF Pro.
   *
   * SF Pro cannot be embedded as a webfont — Apple's license covers UI use in
   * apps and mockups for Apple platforms, not web embedding or redistribution,
   * and no CDN legitimately serves it. It does not need to be embedded: on
   * iOS and macOS `-apple-system` / `BlinkMacSystemFont` resolve to SF Pro
   * natively, with its full 100–900 range AND its optical sizing (iOS switches
   * SF Text -> SF Display above 20pt and tightens tracking automatically). That
   * optical behaviour is most of why native iOS headlines look tighter than any
   * webfont set at the same px, and it is not reproducible with a static font.
   *
   * This app is reached by tapping an NFC tag with a phone, so the phone is the
   * product and the system font is both the fastest (no webfont request, no
   * FOUT) and the most native-feeling choice there.
   *
   * ORDER MATTERS, and was measured rather than assumed. In Chromium on macOS:
   * `-apple-system` does NOT resolve (it is Safari's keyword) and measures
   * identically to a nonexistent family; `system-ui` and `BlinkMacSystemFont`
   * both resolve to the same face. So `system-ui` leads — it is the
   * standardised keyword and the only one that works across Safari, Chrome,
   * Firefox and Android — with the two legacy keywords behind it for older
   * engines. Leading with `-apple-system` "worked" only because Blink's keyword
   * happened to catch it second.
   *
   * Note that SF Pro is unreachable BY NAME: `'SF Pro Text'`, `'SF Pro Display'`
   * and `'.SF NS'` all fail to resolve, because Apple hides the system faces
   * from font enumeration. The keyword is the only route to it.
   *
   * Consequence worth being explicit about: with `system-ui` first, each
   * platform renders its OWN UI font — SF Pro on Apple, Roboto on Android,
   * Segoe UI on Windows — so `Inter` below is a genuine last resort rather than
   * the usual case. That is the deliberate trade: native on every platform,
   * instead of identical on all of them. If cross-platform consistency ever
   * matters more than native feel, move `'Inter'` to the front of this stack —
   * that one edit flips the policy.
   */
  sans: "system-ui, -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",

  /**
   * The family to actually FETCH from Google. `sans` is a stack and cannot be
   * requested; this is the one webfont in it, loaded for the platforms that do
   * not have SF Pro.
   */
  webfont: 'Inter',
} as const;

export const tokens = { border, radius, space, font, elevation, typeface } as const;
